const React = require('react');
const { default: Paper } = require('@mui/material/Paper');
const { default: Styled } = require('styled-components');

const internals = {};

const LimitedSelectMenuPaper = React.forwardRef(function LimitedSelectMenuPaper(props, ref) {

    const {
        PaperRoot,
        ScrollBody,
        Track,
        Thumb
    } = internals;

    const {
        children,
        style = {},
        ...other
    } = props;
    const scrollRef = React.useRef(null);
    const trackRef = React.useRef(null);
    const thumbRef = React.useRef({
        height: 24,
        top: 0
    });
    const dragCleanupRef = React.useRef(null);
    const [thumb, setThumb] = React.useState({
        height: 24,
        top: 0
    });
    const [overflows, setOverflows] = React.useState(false);

    const updateThumb = React.useCallback(() => {

        const el = scrollRef.current;

        if (!el) {
            return;
        }

        const {
            clientHeight,
            scrollHeight,
            scrollTop
        } = el;

        if (!clientHeight || !scrollHeight) {
            setOverflows(false);
            return;
        }

        const hasOverflow = scrollHeight > clientHeight + 1;
        const height = Math.max(24, Math.min(clientHeight, (clientHeight / scrollHeight) * clientHeight));
        const maxTop = Math.max(0, clientHeight - height);
        const top = hasOverflow
            ? maxTop * (scrollTop / (scrollHeight - clientHeight))
            : 0;

        setOverflows(hasOverflow);
        const nextThumb = {
            height,
            top
        };
        thumbRef.current = nextThumb;
        setThumb(nextThumb);
    }, []);

    React.useLayoutEffect(() => {

        const el = scrollRef.current;

        if (!el) {
            return undefined;
        }

        const frame = requestAnimationFrame(updateThumb);
        el.addEventListener('scroll', updateThumb, { passive: true });

        let observer;

        if (typeof ResizeObserver === 'function') {
            observer = new ResizeObserver(updateThumb);
            observer.observe(el);
        }

        return () => {

            cancelAnimationFrame(frame);
            el.removeEventListener('scroll', updateThumb);
            observer?.disconnect();
            dragCleanupRef.current?.();
        };
    }, [children, updateThumb]);

    const scrollToPointer = React.useCallback((clientY, grabOffset) => {

        const el = scrollRef.current;
        const track = trackRef.current;

        if (!el || !track) {
            return;
        }

        const rect = track.getBoundingClientRect();
        const thumbHeight = thumbRef.current.height;
        const maxTop = Math.max(0, rect.height - thumbHeight);
        const offset = Math.min(maxTop, Math.max(0, clientY - rect.top - grabOffset));
        const maxScroll = el.scrollHeight - el.clientHeight;

        if (maxScroll <= 0 || maxTop <= 0) {
            return;
        }

        el.scrollTop = (offset / maxTop) * maxScroll;
    }, []);

    const handleTrackPointerDown = (event) => {

        event.preventDefault();
        event.stopPropagation();

        const track = trackRef.current;

        if (!track) {
            return;
        }

        const clickedThumb = event.target.closest?.('[data-scrollbar-thumb]');
        const grabOffset = clickedThumb
            ? event.clientY - (track.getBoundingClientRect().top + thumbRef.current.top)
            : thumbRef.current.height / 2;

        if (!clickedThumb) {
            scrollToPointer(event.clientY, grabOffset);
        }

        const onMove = (moveEvent) => {

            moveEvent.preventDefault();
            scrollToPointer(moveEvent.clientY, grabOffset);
        };
        const onUp = () => {

            dragCleanupRef.current = null;
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
        };

        dragCleanupRef.current?.();
        dragCleanupRef.current = onUp;
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
    };

    return (
        <PaperRoot
            ref={ref}
            square={false}
            {...other}
            style={{
                ...style,
                height: style.maxHeight,
                maxHeight: style.maxHeight,
                overflow: 'hidden',
                overflowX: 'hidden',
                overflowY: 'hidden'
            }}
        >
            <ScrollBody ref={scrollRef}>
                {children}
            </ScrollBody>
            {overflows && (
                <Track
                    ref={trackRef}
                    data-testid='limited-select-menu-scrollbar'
                    onMouseDown={(event) => event.preventDefault()}
                    onPointerDown={handleTrackPointerDown}
                >
                    <Thumb
                        data-scrollbar-thumb='true'
                        style={{
                            height: `${thumb.height}px`,
                            transform: `translateY(${thumb.top}px)`
                        }}
                    />
                </Track>
            )}
        </PaperRoot>
    );
});

LimitedSelectMenuPaper.displayName = 'LimitedSelectMenuPaper';

module.exports = LimitedSelectMenuPaper;

internals.PaperRoot = Styled(Paper)`
    position: absolute;
    outline: 0;
    overflow: hidden !important;
    display: flex;
    flex-direction: row;
    align-items: stretch;
`;

internals.ScrollBody = Styled.div`
    flex: 1 1 auto;
    min-width: 0;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
        display: none;
        width: 0;
        height: 0;
    }
`;

internals.Track = Styled.div`
    flex: 0 0 12px;
    width: 12px;
    position: relative;
    background: #f1f1f1;
    border-left: 1px solid rgba(0, 0, 0, 0.08);
    cursor: pointer;
    touch-action: none;
`;

internals.Thumb = Styled.div`
    position: absolute;
    top: 0;
    left: 3px;
    width: 6px;
    border-radius: 6px;
    background: #888;
    cursor: grab;

    &:active {
        cursor: grabbing;
    }
`;
